import React, { useEffect, useMemo, useState } from 'react';

import api from '@api/api';
import tos from '@api/tos';
import { Languages } from '@customTypes/Entities';
import { isAuthenticated } from '@features/loginSlice';
import FormFields from '@routes/NewOrder/FormFields';
import { TranslateFormData } from '@routes/NewOrder/model';
import { ROUTES } from '@routes/constants';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button, { BUTTON_MODE, BUTTON_TYPE } from '@components/Button';
import ErrorBox from '@components/ErrorBox';
import Modal from '@components/Modal';
import OrderResume from '@components/OrderResume';
import Spinner from '@components/Spinner';

import styles from './style.module.scss';

interface NewOrderBaseProps {
  type: string;
  selected: number[];
  namedSelected?: { id: number; name: string }[];
  onSend: (data: TranslateFormData) => Promise<unknown>;
  mutationIsError: boolean;
  mutationIsLoading: boolean;
  mutationReset: () => void;
  mutationError: any;
}

const NewOrderBase: React.FC<NewOrderBaseProps> = ({
  type,
  selected,
  onSend,
  mutationIsError,
  mutationError,
  mutationReset,
  mutationIsLoading,
  namedSelected,
}) => {
  const [isModalConfirmOpened, setIsModalConfirmOpened] = useState(false);
  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    formState: { isValid },
  } = useForm<TranslateFormData>({ mode: 'all', defaultValues: { orderId: DateTime.now().toFormat('yyyy-MMM-dd_hh:mm:ss') } });

  const loggedIn = useSelector(isAuthenticated);

  const navigate = useNavigate();

  const { data: languages } = useQuery<Languages>({
    queryKey: ['languages'],
    queryFn: () => api.getLanguages(),
    enabled: loggedIn,
  });

  const { data: serviceTypes } = useQuery<string[]>({
    queryKey: ['serviceTypes'],
    queryFn: () => tos.getServiceTypes(),
    enabled: loggedIn,
  });

  const onSubmit = () => {
    setIsModalConfirmOpened(true);
  };

  const sendData = () => {
    onSend({ ...getValues() })
      .then(() => navigate(ROUTES.ORDERS_SUCCESS))
      .finally(() => setIsModalConfirmOpened(false));
  };

  useEffect(() => {
    if (!languages) {
      return;
    }
    setValue('sourceLanguage', languages.sourceLanguage.key);
  }, [languages, setValue]);

  const sortedLanguages = useMemo(() => {
    if (!languages) {
      return [];
    }
    const sorted = [...languages.targetLanguages];
    sorted.sort((l1, l2) => l1.value.localeCompare(l2.value));
    return sorted;
  }, [languages]);

  return (
    <>
      <header className={styles.header}>
        <h2>New {type} order</h2>
      </header>
      <div className={styles.container}>
        <div className={styles.form}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <FormFields control={control} sortedLanguages={sortedLanguages} isValid={isValid} serviceTypes={serviceTypes} languages={languages} />
          </form>
          <div style={{ width: '70%', minWidth: 600, margin: '10px 0' }}>
            <ErrorBox message={mutationIsError && mutationError?.response?.data} clear={() => mutationReset()} />
          </div>
        </div>
        <div className={styles.resume}>
          <h3>You are submitting the following {type}:</h3>
          {namedSelected && <OrderResume namedSelected={namedSelected} />}
          {!namedSelected && (
            <div className={styles.spinnerContainer}>
              <Spinner />
            </div>
          )}
        </div>
      </div>
      {isModalConfirmOpened && (
        <Modal title={'Confirm your order'} onClose={() => setIsModalConfirmOpened(false)}>
          <div className={styles.modal}>
            <p>
              You are submitting {selected.length} {type} in {getValues().targetLanguages.length} languages. Are you sure you want to continue?
            </p>
            <div className={styles.buttonsContainer}>
              <Button onClick={() => setIsModalConfirmOpened(false)} type={BUTTON_TYPE.ERROR} mode={BUTTON_MODE.OUTLINE} className={styles.submitButton}>
                Cancel
              </Button>
              <Button onClick={() => sendData()} type={BUTTON_TYPE.PRIMARY} className={styles.submitButton}>
                {mutationIsLoading ? <Spinner /> : 'Confirm'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
};

export default NewOrderBase;
